import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from './env';
import { useAuthStore } from './auth-store';

let currentBaseUrl = env.apiBaseUrl;

/** Single-flight: várias requisições 401 simultâneas disparam UM refresh só. */
let refreshInFlight: Promise<{ accessToken: string; refreshToken?: string }> | null = null;

function refreshOnce(baseURL: string, refreshToken: string) {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post<{ accessToken: string; refreshToken?: string }>(
        `${baseURL.replace(/\/$/, '')}/auth/refresh`,
        { refreshToken },
        { timeout: 10000 },
      )
      .then((r) => r.data)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function forceLogout() {
  try {
    useAuthStore.getState().clear();
  } catch {
    // ignore
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
}

function build(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 20000,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return config;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
      const status = error.response?.status;
      const isAuthRoute = original?.url?.includes('/auth/');

      // 401 em rota normal: tenta renovar o token UMA vez e repete a requisição.
      if (status === 401 && original && !original._retried && !isAuthRoute) {
        const { refreshToken } = useAuthStore.getState();
        if (refreshToken) {
          try {
            const novo = await refreshOnce(baseURL, refreshToken);
            await useAuthStore.getState().setSession({
              accessToken: novo.accessToken,
              refreshToken: novo.refreshToken ?? refreshToken,
            });
            original._retried = true;
            original.headers.set('Authorization', `Bearer ${novo.accessToken}`);
            return instance.request(original);
          } catch {
            // refresh falhou — cai para o logout abaixo
          }
        }
        forceLogout();
      } else if (status === 401 && !isAuthRoute) {
        forceLogout();
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

let instance = build(currentBaseUrl);

/**
 * Allows runtime swap of the API base URL — useful when running inside Capacitor APK
 * pointing to a remote server (e.g. https://ponto.pontocertond.com/api/v1).
 */
export function setBaseUrl(url: string) {
  currentBaseUrl = url;
  instance = build(currentBaseUrl);
}

export function getBaseUrl(): string {
  return currentBaseUrl;
}

export const api = {
  get: <T = unknown>(...args: Parameters<AxiosInstance['get']>) => instance.get<T>(...args),
  post: <T = unknown>(...args: Parameters<AxiosInstance['post']>) => instance.post<T>(...args),
  put: <T = unknown>(...args: Parameters<AxiosInstance['put']>) => instance.put<T>(...args),
  patch: <T = unknown>(...args: Parameters<AxiosInstance['patch']>) => instance.patch<T>(...args),
  delete: <T = unknown>(...args: Parameters<AxiosInstance['delete']>) => instance.delete<T>(...args),
  request: <T = unknown>(...args: Parameters<AxiosInstance['request']>) => instance.request<T>(...args),
  raw: () => instance,
};

export default api;
